import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import { Navigation, Pagination, Keyboard } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/swiper-bundle.css'

import styled from '@emotion/styled'
import debounce from '@doveconviene/sf-web-js-sdk/debounce'

import { selectLabel } from '../../../../flux/selectors/i18n'
import useOnWindowResize from '../../../ui/hooks/useOnWindowResize'
import ProductCard from '../../Product/ProductCard'
import ProductsArrowNavigator from './components/productsArrowNavigator'

const StyledContainer = styled.div(({ theme: { colors } }) => ({
  borderBottom: `1px solid ${colors.light_grey}`,
  display: 'flex',
  flexDirection: 'column',
  padding: '5px 0 20px',
}))

const StyledCarousel = styled.div({
  display: 'flex',
  '& > .swiper.swiper-horizontal > .swiper-pagination-bullets': {
    bottom: '0px !important',
  },
  '& > .swiper.swiper-horizontal.swiper-ios > .swiper-button-prev, .swiper-button-next': {
    WebkitTransform: 'translateZ(0)',
  },
  position: 'relative',
})

const StyledHeader = styled.div(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  padding: '10px 0px',
}))

const StyledTitle = styled.span(({ theme: { colors } }) => ({
  textTransform: 'uppercase',
  fontWeight: 'bolder',
  color: colors.mid_grey,
}))

const StyledCta = styled.span(({ theme: { colors } }) => ({
  cursor: 'pointer',
  color: colors.primary,
  fontSize: '12px',
  fontWeight: 'bolder',
}))

const SWIPER_CONFIG = {
  slidesPerView: 2,
  spaceBetween: 30,
  style: {
    width: '100%',
  },
  keyboard: {
    enabled: true,
    onlyInViewport: false,
  },
  pagination: {
    el: '.swiper-pagination',
    dynamicBullets: true,
    clickable: true,
  },
  breakpoints: {
    200: { slidesPerView: 2 },
    480: { slidesPerView: 4 },
    640: { slidesPerView: 6 },
  },
}

const ProductsCarousel = ({
  category,
  products,
  similarProductId,
  containerRef,
  onShowAllClick,
  onProductClick,
  onProductVisible,
}) => {
  const [swiper, setSwiper] = useState(null)
  const viewAllLabel = useSelector(selectLabel('viewAll'))

  const onSwiperInit = swiperInstance => {
    setSwiper(swiperInstance)
  }

  const updateSwiper = debounce(() => {
    if (swiper && !swiper.destroyed) {
      swiper.update()
      swiper.slideTo(0)
    }
  }, 100)

  useOnWindowResize(updateSwiper)

  React.useEffect(() => {
    updateSwiper()
  }, [products, updateSwiper])

  return (
    <StyledContainer data-test="ProductsCarousel">
      <StyledHeader>
        <StyledTitle>{category}</StyledTitle>
        <StyledCta onClick={onShowAllClick}>{viewAllLabel}</StyledCta>
      </StyledHeader>
      <StyledCarousel>
        <ProductsArrowNavigator
          similarProductId={similarProductId}
          swiper={swiper}
          productsTotal={products.length}
        />
        <Swiper
          modules={[Navigation, Pagination, Keyboard]}
          onInit={onSwiperInit}
          style={SWIPER_CONFIG.style}
          keyboard={SWIPER_CONFIG.keyboard}
          slidesPerView={SWIPER_CONFIG.slidesPerView}
          spaceBetween={SWIPER_CONFIG.spaceBetween}
          pagination={SWIPER_CONFIG.pagination}
          breakpoints={SWIPER_CONFIG.breakpoints}
        >
          {
            products.map((product, index) => (
              <SwiperSlide key={product.id}>
                <ProductCard
                  category={category}
                  product={product}
                  onProductClick={() => onProductClick(product, category, index)}
                  onProductVisible={() => onProductVisible(product, category, index)}
                  containerRef={containerRef}
                  isSimilarProductsCarousel
                />
              </SwiperSlide>
            ))
          }
        </Swiper>
      </StyledCarousel>
    </StyledContainer>
  )
}

ProductsCarousel.propTypes = {
  category: PropTypes.string,
  products: PropTypes.arrayOf(PropTypes.shape({})),
  containerRef: PropTypes.shape({}),
  similarProductId: PropTypes.string,
  onShowAllClick: PropTypes.func,
  onProductClick: PropTypes.func,
  onProductVisible: PropTypes.func,
}

ProductsCarousel.defaultProps = {
  category: '',
  products: [],
  containerRef: null,
  similarProductId: '',
  onProductClick: () => false,
  onShowAllClick: () => false,
  onProductVisible: () => false,
}

ProductsCarousel.displayName = 'ProductsCarousel'
ProductsCarousel.whyDidYouRender = true

export default ProductsCarousel
